import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import type { z } from 'zod';

import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { createFormInvalidHandler, formatLiftAPIClientError } from '~/components/form/utils';
import type { Contact } from '~/data/generated/model';
import { type ContactInput, contactInputSchema } from '~/data/schemas/contact';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

// `contactInputSchema` runs `email` and `phone` through `z.preprocess`, so the
// values the inputs hold on the way in are not the parsed values `handleSubmit`
// hands to `onSubmit`. `ContactInput` names the latter.
type ContactFormValues = z.input<typeof contactInputSchema>;

interface ContactEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ContactInput) => Promise<void>;
  contact?: Contact | null;
}

const ContactEditDialog = ({ open, onClose, onSave, contact }: ContactEditDialogProps) => {
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tracks the dialog's open state as of the last render so the reset below
  // can run as a render-time state adjustment (React's documented
  // alternative to synchronous setState-in-effect) instead of an Effect.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSaveError(null);
    }
  }
  const { showSnackbar } = useLAISnackbar();
  const { control, register, handleSubmit, reset } = useForm<
    ContactFormValues,
    unknown,
    ContactInput
  >({
    resolver: zodResolver(contactInputSchema),
    defaultValues: {
      name: contact?.name ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
    },
  });
  const { errors, isSubmitting } = useFormState({ control });

  useEffect(() => {
    if (open) {
      reset({
        name: contact?.name ?? '',
        email: contact?.email ?? '',
        phone: contact?.phone ?? '',
      });
    }
  }, [open, contact, reset]);

  const onSubmit = async (data: ContactInput) => {
    try {
      setSaveError(null);
      await onSave(data);
      onClose();
    } catch (error) {
      setSaveError(formatLiftAPIClientError('Failed to save contact', error));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitleWithCloseButton
        onClose={isSubmitting ? () => {} : onClose}
        title={contact ? 'Edit Contact' : 'Add Contact'}
      />
      <form
        onSubmit={handleSubmit(
          onSubmit,
          createFormInvalidHandler<ContactFormValues>({
            formName: 'ContactEditDialog',
            showSnackbar,
          }),
        )}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          <TextField
            label="Name"
            required
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name')}
          />
          <TextField
            label="Email"
            type="email"
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register('email')}
          />
          <TextField
            label="Phone"
            error={!!errors.phone}
            helperText={
              errors.phone?.message ?? 'For non-US numbers, include country code (e.g. +44)'
            }
            {...register('phone')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting} sx={BUTTON_SX}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={BUTTON_SX}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactEditDialog;
